import React, { useEffect, useRef, useState } from "react";
import { useAuthState } from "~/hooks/authContext";
import "./UnityGame.css";

type UnityGameProps = {
  buildPath: string;
};

interface UnityInstance {
  SendMessage(gameObject: string, methodName: string, parameter?: string): void;
  Quit(): Promise<void>;
}

declare function createUnityInstance(
  canvas: HTMLCanvasElement,
  config: {
    dataUrl: string;
    frameworkUrl: string;
    codeUrl: string;
    streamingAssetsUrl: string;
    webglContextAttributes?: WebGLContextAttributes;
  },
  onProgress?: (progress: number) => void,
): Promise<UnityInstance>;

declare global {
  interface Window {
    unityInstance?: UnityInstance;
    OnBridgeReady?: () => void;
  }
}

export default function UnityGame({ buildPath }: UnityGameProps) {
  const authState = useAuthState();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const unityInstanceRef = useRef<UnityInstance | null>(null);
  const wheelHandlerRef = useRef<((e: WheelEvent) => void) | null>(null);
  const lastBridgeAuthKeyRef = useRef<string | null>(null);
  const bridgeAuthContextRef = useRef<{
    authToken?: string;
    studentId?: string;
  }>({});
  const sendBridgeAuthContextRef = useRef<() => void>(() => {});
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(
    "loading",
  );

  bridgeAuthContextRef.current =
    authState.type === "signedIn"
      ? {
          authToken: authState.user.authToken,
          studentId: authState.user.id ?? authState.user.username,
        }
      : {};

  sendBridgeAuthContextRef.current = () => {
    const unityInstance = unityInstanceRef.current;
    const { authToken, studentId } = bridgeAuthContextRef.current;

    // createUnityInstance resolves only after the player is ready to receive
    // SendMessage calls. Do not wait for OnBridgeReady here: some practice
    // builds wait for the auth context before raising that callback, which
    // leaves the in-game Begin button in a deadlock.
    if (!unityInstance) {
      return;
    }

    if (!authToken) {
      console.warn(
        "Unity bridge is ready, but no signed-in auth token is available.",
      );
      return;
    }

    const bridgeAuthKey = `${studentId ?? ""}:${authToken}`;
    if (lastBridgeAuthKeyRef.current === bridgeAuthKey) {
      return;
    }

    if (studentId) {
      unityInstance.SendMessage("Bridge", "ReceiveStudentId", studentId);
    }
    unityInstance.SendMessage("Bridge", "ReceiveAuthToken", authToken);
    lastBridgeAuthKeyRef.current = bridgeAuthKey;
    console.log("Unity bridge auth context sent.");
  };

  useEffect(() => {
    sendBridgeAuthContextRef.current();
  }, [authState]);

  useEffect(() => {
    if (!canvasRef.current) return;

    let isUnmounted = false;
    let initTimeout: number | undefined;
    lastBridgeAuthKeyRef.current = null;
    const onBridgeReady = () => {
      if (isUnmounted) return;
      console.log("Unity bridge is ready.");
      sendBridgeAuthContextRef.current();
    };
    window.OnBridgeReady = onBridgeReady;

    const gameFolder = buildPath.replace(/^\/+|\/+$/g, "");
    const baseUrl = `${process.env.GATSBY_WEBGL_BASE_URL}/${gameFolder}`;
    const prefix = gameFolder;

    const script = document.createElement("script");
    script.src = `${baseUrl}/Build/${prefix}.loader.js`;
    console.log("WEBGL BASE:", process.env.GATSBY_WEBGL_BASE_URL);
    const canvas = canvasRef.current;
    setLoadProgress(0);
    setLoadState("loading");

    script.onload = () => {
      initTimeout = window.setTimeout(() => {
        if (isUnmounted) return;

        // 🔒 GPU-safe sizing
        const testCanvas = document.createElement("canvas");
        const gl =
          testCanvas.getContext("webgl") || testCanvas.getContext("webgl2");
        const maxSize = gl ? gl.getParameter(gl.MAX_TEXTURE_SIZE) : 4096;

        const MAX_TEXTURE_SIZE = Math.min(maxSize / 2, 4096);

        const cssWidth = canvas.clientWidth || 960;
        const cssHeight = canvas.clientHeight || 600;

        canvas.width = Math.min(cssWidth, MAX_TEXTURE_SIZE);
        canvas.height = Math.min(cssHeight, MAX_TEXTURE_SIZE);

        console.log(
          `Canvas set to ${canvas.width}×${canvas.height} (max GPU ${maxSize})`,
        );

        createUnityInstance(
          canvas,
          {
            dataUrl: `${baseUrl}/Build/${prefix}.data`,
            frameworkUrl: `${baseUrl}/Build/${prefix}.framework.js`,
            codeUrl: `${baseUrl}/Build/${prefix}.wasm`,
            streamingAssetsUrl: `${baseUrl}/StreamingAssets`,
            webglContextAttributes: {
              preserveDrawingBuffer: false,
              powerPreference: "high-performance",
            },
          },
          (progress) => {
            if (isUnmounted) return;
            setLoadProgress(progress);
          },
        )
          .then((unityInstance) => {
            if (isUnmounted) {
              void unityInstance.Quit().catch((err) => {
                console.error("❌ Unity quit after unmount failed:", err);
              });
              return;
            }

            unityInstanceRef.current = unityInstance;
            window.unityInstance = unityInstance;
            setLoadProgress(1);
            setLoadState("ready");
            console.log("✅ Unity instance created");
            sendBridgeAuthContextRef.current();

            const handleWheel = (e: WheelEvent) => {
              // Allow page scroll while hovering Unity
              window.scrollBy({
                top: e.deltaY,
                behavior: "auto",
              });
            };

            canvas.addEventListener("wheel", handleWheel, {
              passive: true,
            });
            wheelHandlerRef.current = handleWheel;
          })
          .catch((err) => {
            if (!isUnmounted) {
              setLoadState("error");
            }
            console.error("❌ Unity init failed:", err);
          });
      }, 100);
    };

    script.onerror = () => {
      setLoadState("error");
      console.error("❌ Failed to load Unity loader script");
    };

    document.body.appendChild(script);

    return () => {
      isUnmounted = true;
      if (initTimeout) {
        window.clearTimeout(initTimeout);
      }

      const currentWheelHandler = wheelHandlerRef.current;
      if (currentWheelHandler) {
        canvas.removeEventListener("wheel", currentWheelHandler);
      }
      wheelHandlerRef.current = null;

      const currentUnityInstance = unityInstanceRef.current;
      if (currentUnityInstance) {
        void currentUnityInstance
          .Quit()
          .then(() => {
            console.log("🛑 Unity instance quit cleanly");
          })
          .catch((err) => {
            console.error("❌ Unity quit failed:", err);
          })
          .finally(() => {
            unityInstanceRef.current = null;
            if (window.unityInstance === currentUnityInstance) {
              delete window.unityInstance;
            }
          });
      } else {
        delete window.unityInstance;
      }

      if (window.OnBridgeReady === onBridgeReady) {
        delete window.OnBridgeReady;
      }
      lastBridgeAuthKeyRef.current = null;
      script.parentNode?.removeChild(script);
    };
  }, [buildPath]);

  useEffect(() => {
    console.log("🎮 UnityGame MOUNT");
    return () => {
      console.log("🧹 UnityGame UNMOUNT");
    };
  }, [buildPath]);

  return (
    <div className="unity-container">
      {loadState !== "ready" && (
        <div
          className={`unity-loading-shell unity-loading-shell--${loadState}`}
        >
          <div className="unity-loading-panel">
            <div className="unity-loading-label">
              {loadState === "error"
                ? "Unable to load activity"
                : "Loading activity..."}
            </div>
            {loadState === "error" ? (
              <p className="unity-loading-message">
                Please refresh the page and try again.
              </p>
            ) : (
              <>
                <div
                  className="unity-loading-bar"
                  role="progressbar"
                  aria-label="Unity loading progress"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(loadProgress * 100)}
                >
                  <div
                    className="unity-loading-bar-fill"
                    style={{ width: `${Math.round(loadProgress * 100)}%` }}
                  />
                </div>
                <div className="unity-loading-progress">
                  {Math.round(loadProgress * 100)}%
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <canvas
        ref={canvasRef}
        id="unity-canvas"
        className={
          loadState === "ready"
            ? "unity-canvas--ready"
            : "unity-canvas--loading"
        }
      />
    </div>
  );
}
